import { useState, useEffect } from 'react';
import { Table, Card, Button, Tag, Space, message } from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import DataMatrixImage from '../../components/marking/DataMatrixImage';
import BrandLogo from '../../components/BrandLogo';
import { markingApi, type MarkingCode } from '../../api/marking';
import { saveMarksToFolder } from '../../utils/marksPdf';
import useIsMobile from '../../hooks/useIsMobile';

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ready:       { label: 'Готова к продаже', color: 'green'  },
  reserved:    { label: 'В заказе',         color: 'purple' },
  sold:        { label: 'Продана',          color: 'default'},
  off_balance: { label: 'Вне баланса 1С',   color: 'orange' },
  written_off: { label: 'Списана',          color: 'red'    },
};

const MarkingHistoryPage: React.FC = () => {
  const isMobile = useIsMobile();
  const [scans, setScans] = useState<MarkingCode[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [savingMarks, setSavingMarks] = useState(false);
  const pageSize = 20;

  const load = async (p = page, order = sortOrder) => {
    setLoading(true);
    try {
      const [listRes, countRes] = await Promise.all([
        markingApi.listScans((p - 1) * pageSize, pageSize, undefined, order),
        markingApi.countScans(),
      ]);
      setScans(listRes.data as MarkingCode[]);
      setTotal(countRes.data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [page, sortOrder]);

  const handleSaveMarks = async () => {
    if (selectedCodes.length === 0) {
      message.info('Выберите марки для сохранения');
      return;
    }
    const toSave = scans.filter((s) => selectedCodes.includes(s.marking_code));
    setSavingMarks(true);
    try {
      const n = await saveMarksToFolder(toSave);
      if (n > 0) {
        message.success(`Сохранено файлов: ${n}`);
      }
    } catch (e: any) {
      message.error('Ошибка сохранения: ' + (e?.message || 'неизвестная'));
    } finally {
      setSavingMarks(false);
    }
  };

  const columns: ColumnsType<MarkingCode> = [
    {
      title: 'Товар',
      key: 'product',
      render: (_, r) => r.product_name
        ? (
          <Space size={8}>
            {r.brand && <BrandLogo brand={r.brand} />}
            <span>{r.product_name}</span>
          </Space>
        )
        : <Tag color="orange">GTIN: {r.gtin || '—'}</Tag>,
    },
    {
      title: 'Тара',
      dataIndex: 'container_size',
      width: 100,
      render: (v: string | null) => v || '—',
    },
    {
      title: 'Статус',
      key: 'status',
      width: 160,
      render: (_, r) => {
        const info = STATUS_LABELS[r.status] || { label: r.status, color: 'default' };
        return <Tag color={info.color}>{info.label}</Tag>;
      },
    },
    {
      title: 'Серийный номер',
      dataIndex: 'serial_number',
      width: 160,
      render: (v: string | null) => <code>{v || '—'}</code>,
    },
    {
      title: 'Дата',
      dataIndex: 'scanned_at',
      width: 150,
      sorter: true,
      sortOrder: sortOrder === 'asc' ? 'ascend' : 'descend',
      render: (v: string) => new Date(v).toLocaleString('ru-RU'),
    },
    {
      title: 'Кто',
      dataIndex: 'scanned_by_name',
      width: 140,
    },
  ];

  return (
    <Card
      title="История сканирования"
      extra={
        <Space>
          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveMarks}
            loading={savingMarks}
            disabled={selectedCodes.length === 0}
          >
            Сохранить марки{selectedCodes.length > 0 ? ` (${selectedCodes.length})` : ''}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={() => load()}>Обновить</Button>
        </Space>
      }
    >
      <Table
        rowKey="marking_code"
        columns={columns}
        dataSource={scans}
        loading={loading}
        rowSelection={{
          selectedRowKeys: selectedCodes,
          onChange: (keys) => setSelectedCodes(keys as string[]),
        }}
        onChange={(_pag, _flt, sorter) => {
          const s = Array.isArray(sorter) ? sorter[0] : sorter;
          // AntD Table имеет 3-state цикл сортировки по умолчанию:
          //   null → ascend → descend → null → ...
          // При текущем descend клик возвращает sorter.order=undefined
          // (cleared) — интерпретируем как «переключить на противоположное».
          let next: 'asc' | 'desc';
          if (s?.order === 'ascend') next = 'asc';
          else if (s?.order === 'descend') next = 'desc';
          else next = sortOrder === 'desc' ? 'asc' : 'desc';
          if (next !== sortOrder) {
            setPage(1);
            setSortOrder(next);
          }
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showSizeChanger: false,
          showTotal: (t) => `Всего: ${t}`,
        }}
        onRow={(record) => ({
          onClick: (e) => {
            if ((e.target as HTMLElement).closest('.ant-table-selection-column')) return;
            setExpandedKeys((prev) => prev.includes(record.marking_code) ? [] : [record.marking_code]);
          },
          style: { cursor: 'pointer' },
        })}
        expandable={{
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
          expandedRowRender: (record) => (
            <div style={{ padding: '16px 0' }}>
              <div style={isMobile ? {
                position: 'sticky', left: '50vw', transform: 'translateX(-50%)',
                display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              } : {
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
              }}>
                <DataMatrixImage code={record.raw_code} scale={3} />
                <div>
                  <Tag>GTIN: {record.gtin || '—'}</Tag>
                  <Tag>S/N: {record.serial_number || '—'}</Tag>
                </div>
              </div>
            </div>
          ),
        }}
        size="small"
      />
    </Card>
  );
};

export default MarkingHistoryPage;
